import { useCallback, useEffect, useState } from "react";
import { useCoarsePointer, useGuidedOptional, useTourState, useTourStoreApi } from "@heroncendre/player";
import chainesJson from "../chaines.json";
import { etatInfos } from "./etatInfos.js";
import type { EvenementInfos } from "./logiqueInfos.js";

export type CleInfo = Extract<keyof typeof chainesJson.fr, `info${string}`>;

const voixActive = (etat: string) => etat === "narration" || etat === "free";

/**
 * Les actions du menu d'informations, branchées sur la voix off du moteur. Toutes les
 * instances partagent `etatInfos` : un bouton ⓘ, la feuille et les crédits n'ont pas à se
 * connaître. C'est ici — et nulle part ailleurs — que la voix est arrêtée et relancée.
 */
export function useInfos() {
  const store = useTourStoreApi();
  const language = useTourState((s) => s.context.language);
  const mobile = useCoarsePointer();
  const guide = useGuidedOptional();

  const t = useCallback(
    (cle: CleInfo): string => {
      const table = (chainesJson as Record<string, Partial<Record<CleInfo, string>>>)[language] ?? chainesJson.fr;
      return table[cle] ?? chainesJson.fr[cle];
    },
    [language],
  );

  const appliquer = useCallback(
    (evenement: EvenementInfos, origine?: HTMLElement | null) => {
      const avant = store.getState();
      const jouait = avant.narration.playing && voixActive(avant.guided.state);
      const effet = etatInfos.appliquer(evenement, jouait, origine);
      if (effet === "pause") store.getState().setNarrationPlaying(false);
      // Le chapitre a pu se terminer entre-temps : on ne relance une voix que si elle a encore lieu d'être.
      else if (effet === "reprise" && voixActive(store.getState().guided.state)) store.getState().setNarrationPlaying(true);
    },
    [store],
  );

  return {
    t,
    language,
    mobile,
    basculerMenu: (origine: HTMLElement | null) => appliquer({ type: "basculerMenu" }, origine),
    fermerMenu: () => appliquer({ type: "fermerMenu" }),
    /** Clic droit (ordinateur) : ouvre le menu au curseur, ou le déplace s'il est déjà ouvert. */
    ouvrirAuPoint: (x: number, y: number) => {
      etatInfos.ancrer({ x, y });
      if (!etatInfos.lire().session.menu) appliquer({ type: "basculerMenu" });
    },
    ouvrirCredits: () => appliquer({ type: "ouvrirCredits", garderMenu: mobile }),
    retourCredits: () => appliquer({ type: "retourCredits" }),
    fermerTout: () => appliquer({ type: "fermerTout" }),
    lien: () => appliquer({ type: "lien" }),
    /**
     * « Accueil » : tout se ferme, un parcours en cours est arrêté (voix coupée, guide quitté — sinon il
     * continuerait sous l’accueil), puis l’hôte repasse à l’écran de choix.
     */
    allerAccueil: (surAccueil: () => void) => {
      appliquer({ type: "fermerTout" });
      const etat = store.getState();
      if (etat.guided.state !== "idle") {
        guide?.controller.audio().pause();
        etat.exitGuided();
      }
      surAccueil();
    },
    /** « Commandes de la visite » : le menu se ferme, l'indice « Regardez autour de vous » se rejoue. */
    rejouerIndice: () => {
      appliquer({ type: "fermerMenu" });
      etatInfos.demanderRejeu();
    },
  };
}

/** Plein écran (Fullscreen API) : disponibilité, état suivi, bascule. Absent sur iPhone Safari. */
export function usePleinEcran() {
  const disponible = typeof document !== "undefined" && document.fullscreenEnabled === true;
  const [plein, setPlein] = useState(() => typeof document !== "undefined" && document.fullscreenElement != null);

  useEffect(() => {
    const surChangement = () => setPlein(document.fullscreenElement != null);
    document.addEventListener("fullscreenchange", surChangement);
    return () => document.removeEventListener("fullscreenchange", surChangement);
  }, []);

  const basculer = useCallback(() => {
    if (!disponible) return;
    // Un refus (geste manquant, politique de l'iframe) n'a rien à dire au visiteur : l'état reste celui de l'écran.
    const promesse = document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen();
    promesse.catch(() => undefined);
  }, [disponible]);

  return { disponible, plein, basculer };
}
